import React, { useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { ConnectedFlightsManager } from './FlightsManager';
import { AirlineFilterSelector } from '../AirlineFilterSelector/AirlineFilterSelector';
import { TripTypeSelectorMobile } from '../TripTypeSelector/TripTypeSelectorMobile';
import { SegmentManagerMobile } from './Segment/SegmentManager';
import { PaxSelectorMobile } from '../PaxSelector/PaxSelectorMobile';
import { ResidentDiscount } from '../ResidentDiscount/ResidentDiscountMobile';
import { SlideGroupContextProvider } from '../../components/SlideManager';
import { ABUtils, Config } from '../../utils';
import { prefetchAssets } from '../../utils/PrefetchUtils';
import { isPrimeUserSelector } from '../../redux/userAuthentication/selectors';
import { ButtonCTA } from './MobileFragments/ButtonCTA';
import { isCurrentSearchPopulated } from '../../redux/currentSearch/selectors';
import {
    getWrapperClassNames,
    getTripTypeWrapperClassNames,
    getButtonWrapperClassNames,
    getSecondaryButtonClassNames,
    getFlightsManagerClassNames
} from './FlightsManagerMobileUtils';

export const SegmentManagerAndPaxSelector = ({ isHotelOptionEnabled }) => (
    <React.Fragment>
        <SegmentManagerMobile />
        <PaxSelectorMobile isHotelOptionEnabled={isHotelOptionEnabled} />
    </React.Fragment>
);

const HotelWidgetMessage = ({ isShowHotelWidget, isReactPrimeHotelsMobile, isOneEntryPoint, t, messageRef }) => {
    if (!isShowHotelWidget || !isReactPrimeHotelsMobile || isOneEntryPoint) return null;

    return (
        <div
            id="odf-message-feedback-informative-prime-arrow"
            ref={messageRef}
            className="odf-message-block odf-message-feedback-informative-prime odf-message-with-arrow-wrapper
            odf-message-with-arrow-top-middle-left odf-space-inner-s"
            style={{ marginTop: 16, marginLeft: 16, marginRight: 16, borderRadius: 4 }}
        >
            <div className="odf-message-content">
                <div dangerouslySetInnerHTML={{ __html: t('prime.home.hotels.mobile.tab.title') }} />
            </div>
        </div>
    );
};

const manageHotelWidgetEventListener = (isShowHotelWidget, isReactPrimeHotelsMobile, homePageMenu, removeMessage) => {
    if (isShowHotelWidget && isReactPrimeHotelsMobile && homePageMenu) {
        homePageMenu.addEventListener('touchstart', removeMessage, true);
    }
    return () => {
        if (isShowHotelWidget && isReactPrimeHotelsMobile && homePageMenu) {
            homePageMenu.removeEventListener('touchstart', removeMessage, true);
        }
    };
};

export const FlightsManagerMobile = ({ isHotelOptionEnabled, rootDataAttr }) => {
    prefetchAssets();
    const isPrimeUser = useSelector((state) => isPrimeUserSelector(state));
    const hasCurrentSearchPopulated = useSelector((state) => isCurrentSearchPopulated(state));
    const isShowHotelWidget = Config.get('isShowHotelWidget') && isPrimeUser;
    const isOneEntryPointEnabled = Config.get('isOneEntryPointEnabled');
    const { isOneEntryPoint, isCssVariationMobile, isReactPrimeHotelsMobile, isOneEntryPointAppBanner } = ABUtils.getABs();
    const showOneEntryPoint = isOneEntryPointEnabled && !hasCurrentSearchPopulated;
    const { airlinefilteriata: airlineIata, airlinefiltername: airlineName } = rootDataAttr;
    const { t } = useTranslation();
    const messageRef = useRef(null);
    const homePageMenu = document.getElementsByClassName('od-menu-products-wrapper')[0];

    const removeMessage = () => {
        if (messageRef && messageRef.current) {
            messageRef.current.style.display = 'none';
            homePageMenu.removeEventListener('touchstart', removeMessage, true);
        }
    };
    useEffect(() => {
        return manageHotelWidgetEventListener(isShowHotelWidget, isReactPrimeHotelsMobile, homePageMenu, removeMessage);
    }, []);

    const wrapperClassNames = getWrapperClassNames(isCssVariationMobile, showOneEntryPoint, isOneEntryPoint, isOneEntryPointAppBanner);
    const tripTypeWrapperClassNames = getTripTypeWrapperClassNames(isCssVariationMobile);
    const buttonWrapperClassNames = getButtonWrapperClassNames(isCssVariationMobile, airlineIata, airlineName);
    const secondaryButtonClassNames = getSecondaryButtonClassNames(isCssVariationMobile);
    const flightsManagerClass = getFlightsManagerClassNames(showOneEntryPoint);

    return (
        <div className={flightsManagerClass}>
            <HotelWidgetMessage
                isShowHotelWidget={isShowHotelWidget}
                isReactPrimeHotelsMobile={isReactPrimeHotelsMobile}
                isOneEntryPoint={isOneEntryPoint || isOneEntryPointAppBanner}
                t={t}
                messageRef={messageRef}
            />
            <ConnectedFlightsManager
                rootDataAttr={rootDataAttr}
                render={({ tripType }, isCurrentSearchPopulated) => (
                    <React.Fragment>
                        <div className={wrapperClassNames}>
                            {(!showOneEntryPoint || showOneEntryPoint && isCurrentSearchPopulated) && (
                                <div className={tripTypeWrapperClassNames}>
                                    <TripTypeSelectorMobile />
                                </div>
                            )}
                            <SlideGroupContextProvider>
                                <SegmentManagerAndPaxSelector isHotelOptionEnabled={isHotelOptionEnabled} />
                            </SlideGroupContextProvider>

                            {(!showOneEntryPoint && airlineIata && airlineName) && (
                                <div className="odf-flex-item odf-flex-item-align-center">
                                    <AirlineFilterSelector airlineIata={airlineIata} airlineName={airlineName} />
                                </div>
                            )}
                            {(!showOneEntryPoint || showOneEntryPoint && isCurrentSearchPopulated) && (
                                <>
                                    <ResidentDiscount />
                                    <ButtonCTA
                                        isHotelOptionEnabled={isHotelOptionEnabled}
                                        isSearchFragmentPage={false}
                                        tripType={tripType}
                                        buttonWrapperClassNames={buttonWrapperClassNames}
                                        secondaryButtonClassNames={secondaryButtonClassNames}
                                    />
                                </>
                            )}
                        </div>
                    </React.Fragment>
                )}
            />
        </div>
    );
};

FlightsManagerMobile.propTypes = {
    isHotelOptionEnabled: PropTypes.bool,
};

FlightsManagerMobile.defaultProps = {
    isHotelOptionEnabled: true,
    rootDataAttr: {
        airlinefilteriata: null,
        airlinefiltername: null,
    }
};
